import * as React from 'react'
import { type ProductSearchParams } from '../components/search/utils/types'
import { useDeepMemo } from '../../../utils/react/react.hooks'
import useGetRetailerProductsVariables from './useGetRetailerProductsVariables.hooks'
import {
  EntityType,
  type ProblemType,
  useCreateCptTicketMutation,
  useGetRetailerProductsForCptQuery,
} from './index'

interface Props {
  searchParams: ProductSearchParams
  productIds: Array<string> | null
  description: string | null
  problemType: ProblemType
}
const useCreateCatalogProblemTrackerTicket = ({
  searchParams,
  productIds,
  description,
  problemType,
}: Props) => {
  const [triggered, setTriggered] = React.useState<boolean>(false)
  const trigger = React.useCallback(() => setTriggered(true), [setTriggered])
  const rpVariables = useGetRetailerProductsVariables(searchParams)

  // skip loading product ids if this function has not been triggered, if the search query has an error (i.e. skip is true), or this hook was provided with product ids directly
  const skipLoadingProductIds = React.useMemo(
    () => !triggered || rpVariables.skip || (!!productIds && productIds.length !== 0),
    [triggered, rpVariables.skip, productIds]
  )
  // Get the required product ids (unless skip is true)
  const {
    data: rpData,
    loading: rpLoading,
    error: rpError,
  } = useGetRetailerProductsForCptQuery({
    variables: rpVariables.result,
    skip: skipLoadingProductIds,
  })

  // build entity ids for creating CPT ticket based on either provided or fetched product ids
  const entityIds = useDeepMemo(() => {
    if (productIds?.length) {
      return productIds.map(p => ({
        productId: { locale: searchParams.locale, productId: p },
      }))
    }
    if (!rpData?.retailerToolsServiceGetRetailerProducts?.results) {
      return []
    }
    return rpData.retailerToolsServiceGetRetailerProducts.results.map(p => ({
      productId: { locale: searchParams.locale, productId: p.productId },
    }))
  }, [productIds, rpData?.retailerToolsServiceGetRetailerProducts?.results, searchParams.locale])

  // Skip filing the ticket if this hook hasn't been triggered, the above product fetching hook is still loading or has errored, or there are no entities to report on.
  const skipFilingTicket = React.useMemo(
    () => rpVariables.skip || !triggered || rpLoading || rpError || entityIds.length === 0,
    [rpVariables.skip, triggered, rpLoading, rpError, entityIds.length]
  )

  // Build the inputs for creating a ticket
  const cptTicketVars = React.useMemo(
    () => ({
      variables: {
        data: {
          client: {
            appName: 'IPP',
            userType: 'IPP',
            //userId injected by mesh service
          },
          entityType: EntityType.EntityTypeProductIds,
          entityIds: entityIds,
          problemType: problemType,
          description: description,
          retailerId: searchParams.retailerId,
        },
      },
    }),
    [entityIds, problemType, description, searchParams.retailerId]
  )

  // build the hook
  const [createCptTicketMutation, { data, loading: loadingCPT, error: errorCPT }] =
    useCreateCptTicketMutation(cptTicketVars)

  // If ready, log the ticket and immediately reset the triggered flag so we dont log it again.
  if (!skipFilingTicket) {
    createCptTicketMutation()
    setTriggered(false)
  }

  // return the return value
  return React.useMemo(
    () => ({
      cptTicketId: data?.catalogProblemTrackerServiceCreateTicket?.cptTicketId,
      loading: loadingCPT || rpLoading,
      error:
        loadingCPT || rpLoading
          ? null
          : rpVariables?.searchError?.toString() || rpError?.message || errorCPT?.message,
      trigger,
    }),
    [
      data?.catalogProblemTrackerServiceCreateTicket?.cptTicketId,
      loadingCPT,
      rpLoading,
      rpVariables?.searchError,
      rpError?.message,
      errorCPT?.message,
      trigger,
    ]
  )
}

export default useCreateCatalogProblemTrackerTicket
